/**
 * MessageModal - Modale message standardisée (SUCCESS, ERROR, WARNING, INFO)
 * Universal Cold Plannit® V3.7
 * ✅ 02 MARS 2026 : Design unifié avec NotificationModal (couleurs vives, animations, auto-close)
 *
 * API (inchangée pour rétrocompatibilité) :
 *   isOpen, type, title, message, buttonLabel, onClose
 */

import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MEDIA_QUERIES } from '../../styles/responsive/unifiedBreakpoints';
import { colors } from '../../styles/colors';

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.9); }
  to   { opacity: 1; transform: scale(1);   }
`;

const fadeOut = keyframes`
  from { opacity: 1; transform: scale(1);   }
  to   { opacity: 0; transform: scale(0.9); }
`;

const BG_COLORS = {
  success: 'rgb(0, 200, 83)',
  error:   'rgb(229, 57, 53)',
  warning: 'rgb(255, 152, 0)',
  info:    'rgb(23, 162, 184)',
  default: 'rgb(0, 123, 255)',
};

const TEXT_COLORS = {
  success: 'rgb(0, 200, 83)',
  error:   'rgb(229, 57, 53)',
  warning: 'rgb(255, 152, 0)',
  info:    'rgb(23, 162, 184)',
  default: 'rgb(0, 123, 255)',
};

const ICONS = {
  success: '✅',
  error:   '❌',
  warning: '⚠️',
  info:    'ℹ️',
};

const DEFAULT_TITLE_KEYS = {
  success: 'modal.success',
  error: 'modal.error',
  warning: 'modal.warning',
  info: 'modal.info'
};

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: ${props => props.$closing ? fadeOut : fadeIn} 0.2s ease-out;
`;

const Modal = styled.div`
  background: ${props => (props.$variant === 'auth' ? colors.primary : (BG_COLORS[props.$type] || BG_COLORS.default))};
  border-radius: 16px;
  padding: 32px;
  min-width: 500px;
  max-width: 600px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  animation: ${props => props.$closing ? fadeOut : fadeIn} 0.3s ease-out;
  border: ${props => (props.$variant === 'auth' ? '1px solid rgba(255, 255, 255, 0.15)' : 'none')};

  ${MEDIA_QUERIES.mobile} {
    min-width: 90%;
    max-width: 95%;
    margin: 16px;
    padding: 16px;
    border-radius: 12px;
  }

  ${MEDIA_QUERIES.mobileXS} {
    padding: 12px;
    margin: 8px;
  }

  ${MEDIA_QUERIES.tablet} {
    min-width: 400px;
    max-width: 500px;
    padding: 24px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  ${MEDIA_QUERIES.mobile}  { gap: 10px; margin-bottom: 12px; }
  ${MEDIA_QUERIES.mobileXS}{ gap: 8px;  margin-bottom: 10px; }
`;

const Icon = styled.div`
  font-size: 48px;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: white;
  flex-shrink: 0;

  ${MEDIA_QUERIES.mobile}  { width: 48px; height: 48px; font-size: 36px; }
  ${MEDIA_QUERIES.mobileXS}{ width: 40px; height: 40px; font-size: 28px; }
`;

const Title = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: white;

  ${MEDIA_QUERIES.mobile}  { font-size: 18px; }
  ${MEDIA_QUERIES.mobileXS}{ font-size: 16px; }
`;

const Message = styled.p`
  margin: 0 0 24px 0;
  font-size: 15px;
  line-height: 1.6;
  color: white;
  white-space: pre-line;

  ${MEDIA_QUERIES.mobile}  { margin-bottom: 16px; font-size: 14px; line-height: 1.5; }
  ${MEDIA_QUERIES.mobileXS}{ margin-bottom: 12px; font-size: 13px; }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;

  ${MEDIA_QUERIES.mobile} { gap: 8px; flex-direction: column-reverse; }
  ${MEDIA_QUERIES.tablet} { flex-direction: row; }
`;

const PrimaryButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => (props.$variant === 'auth' ? colors.buttonPrimary : 'white')};
  color: ${props => (props.$variant === 'auth' ? colors.white : (TEXT_COLORS[props.$type] || TEXT_COLORS.default))};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    background: ${props => (props.$variant === 'auth' ? colors.buttonPrimaryDark : 'rgba(255,255,255,0.9)')};
  }
  &:active{ transform: translateY(0); }

  ${MEDIA_QUERIES.mobile} { padding: 14px 20px; width: 100%; font-size: 16px; min-height: 44px; }
  ${MEDIA_QUERIES.mobileXS}{ padding: 12px 16px; font-size: 14px; }
`;

/**
 * MessageModal — rétrocompatible avec l'ancien API, design unifié avec NotificationModal.
 *
 * Props:
 *   isOpen      {boolean}   — afficher/masquer
 *   type        {string}    — 'success' | 'error' | 'warning' | 'info'
 *   title       {string}    — titre (optionnel, défaut selon type)
 *   message     {string}    — texte du message
 *   buttonLabel {string}    — label bouton (défaut: 'OK')
 *   onClose     {function}  — callback fermeture
 */
const MessageModal = ({
  isOpen,
  type = 'info',
  variant = 'default',
  title,
  message,
  buttonLabel,
  onClose,
}) => {
  const { t } = useTranslation();
  const [closing, setClosing] = React.useState(false);
  const resolvedButtonLabel = buttonLabel || t('common.ok');
  const closeTimerRef = React.useRef(null);
  const mountedRef = React.useRef(true);
  // Compat : montage `{cond && <MessageModal />}` sans prop isOpen → considéré ouvert
  const effectivelyOpen = isOpen !== false;

  // Nettoyage au démontage — évite les modals fantômes inter-route
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  React.useEffect(() => {
    if (effectivelyOpen) setClosing(false);
  }, [effectivelyOpen]);

  const handleClose = React.useCallback(() => {
    if (!mountedRef.current) return;
    setClosing(true);
    closeTimerRef.current = setTimeout(() => {
      if (mountedRef.current && onClose) onClose();
    }, 300);
  }, [onClose]);

  // Auto-close : success 2 s, info 3 s
  React.useEffect(() => {
    if (!effectivelyOpen) return;
    const durations = { success: 2000, info: 3000 };
    const delay = durations[type];
    if (!delay) return;
    const timer = setTimeout(handleClose, delay);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectivelyOpen, type]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  if (!effectivelyOpen) return null;

  return (
    <Overlay $closing={closing} onClick={handleOverlayClick}>
      <Modal $closing={closing} $type={type} $variant={variant}>
        <Header>
          <Icon $type={type}>{ICONS[type] || 'ℹ️'}</Icon>
          <Title>{title || t(DEFAULT_TITLE_KEYS[type] || 'modal.info')}</Title>
        </Header>

        <Message>{message}</Message>

        <Actions>
          <PrimaryButton $type={type} $variant={variant} onClick={handleClose}>
            {resolvedButtonLabel}
          </PrimaryButton>
        </Actions>
      </Modal>
    </Overlay>
  );
};

export default MessageModal;